import { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  Text,
  TextField,
  Select,
  Checkbox,
  Button,
  Banner,
  SkeletonBodyText,
  Box,
  Divider,
  Layout,
} from '@shopify/polaris';
import { clinicsApi, productsApi } from '@/services';
import type { RentalProductConfig } from '@/services/products';
import WrapPicker, { type WrapSelection } from '@/components/WrapPicker';
import AddOnPicker, { type AddOnSelection } from '@/components/AddOnPicker';

// Internal staff may book below the public 2-week minimum (UAT round-4):
// clinics/patients stay clamped to the product minimum in their own portal.
const WEEKS_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  label: i === 0 ? '1 (below clinic minimum — internal only)' : `${i + 1}`,
  value: `${i + 1}`,
}));

export default function AdminCreateClinicOrder() {
  const { id: clinicId } = useParams();
  const navigate = useNavigate();

  const { data: clinic } = useQuery({
    queryKey: ['clinic', clinicId],
    queryFn: () => clinicsApi.getById(clinicId!),
    enabled: !!clinicId,
  });

  const { data: patientsData, isLoading: patientsLoading } = useQuery({
    queryKey: ['clinic-patients', clinicId],
    queryFn: () => clinicsApi.getPatients(clinicId!, { limit: 200 }),
    enabled: !!clinicId,
  });

  const { data: productsRaw, isLoading: productsLoading } = useQuery<
    RentalProductConfig[]
  >({
    queryKey: ['products-all'],
    queryFn: () => productsApi.list(),
  });

  const machines = useMemo(
    () =>
      (productsRaw ?? []).filter(
        (p) => p.status === 'ACTIVE' && p.rentalConfig.isRentalProduct,
      ),
    [productsRaw],
  );

  // Wraps come from the tag-filtered endpoint (vendor:"Game Ready" +
  // tag:rental-wrap) — deriving them as "any active non-rental product"
  // leaked the entire catalog into the wrap picker.
  const { data: wrapsRaw, isLoading: wrapsLoading } = useQuery<
    RentalProductConfig[]
  >({
    queryKey: ['products-wraps'],
    queryFn: () => productsApi.wraps(),
  });

  const wraps = useMemo(
    () =>
      [...(wrapsRaw ?? [])].sort((a, b) => a.title.localeCompare(b.title)),
    [wrapsRaw],
  );

  // Form state
  const [patientId, setPatientId] = useState('');
  const [productId, setProductId] = useState('');
  const [variantId, setVariantId] = useState('');
  const [surgeryDate, setSurgeryDate] = useState('');
  const [rentalWeeks, setRentalWeeks] = useState('2');
  const [isBilateral, setIsBilateral] = useState(false);
  const [isClinicFulfil, setIsClinicFulfil] = useState(false);
  const [shippingAddressType, setShippingAddressType] = useState<'clinic' | 'patient'>('patient');
  const [specialInstructions, setSpecialInstructions] = useState('');

  // Wrap state — the shared picker resolves product + variant to a selection
  const [wrapSelection, setWrapSelection] = useState<WrapSelection | null>(null);

  // Add-ons (clinic orders price at wholesale; server re-verifies prices)
  const { data: addOnProducts } = useQuery({
    queryKey: ['addon-products'],
    queryFn: () => productsApi.addons(),
  });
  const [addOnSelections, setAddOnSelections] = useState<AddOnSelection[]>([]);

  const product = useMemo(
    () => machines.find((p) => p.shopifyProductId === productId),
    [machines, productId],
  );
  const variants = product?.variants || [];
  const selectedVariant = variants.find((v) => v.id === variantId) || variants[0];

  const weeklyRateDollars = product?.rentalConfig.wholesaleWeeklyRateCents
    ? product.rentalConfig.wholesaleWeeklyRateCents / 100
    : product?.rentalConfig.weeklyRateCents
      ? product.rentalConfig.weeklyRateCents / 100
      : 0;
  const bilateralPriceDollars = product?.rentalConfig.wholesaleBilateralPriceCents
    ? product.rentalConfig.wholesaleBilateralPriceCents / 100
    : product?.rentalConfig.bilateralPriceCents
      ? product.rentalConfig.bilateralPriceCents / 100
      : 0;
  const minWeeks = product?.rentalConfig.minRentalWeeks || 2;

  const weeks = parseInt(rentalWeeks, 10) || minWeeks;
  const rentalTotal = weeklyRateDollars * weeks;
  const bilateralTotal = isBilateral ? bilateralPriceDollars : 0;
  const subtotal = rentalTotal + bilateralTotal;

  const wrapValid = !!wrapSelection;
  const isValid =
    !!patientId && !!product && !!surgeryDate && weeks >= 1 && wrapValid;

  const createMutation = useMutation({
    mutationFn: () => {
      if (!product || !wrapSelection) throw new Error('Product and wrap required');
      const variant = selectedVariant || variants[0];
      return clinicsApi.adminCreateOrder(clinicId!, {
        patientId,
        productId: product.shopifyProductId,
        productName: product.title,
        productSku: variant?.sku || '',
        variantId: variant?.id || '',
        size: variant?.title || '',
        weeklyRate: weeklyRateDollars,
        surgeryDate,
        rentalWeeks: weeks,
        isBilateral,
        bilateralPrice: bilateralPriceDollars,
        isClinicFulfil,
        shippingAddressType,
        specialInstructions: specialInstructions || undefined,
        addOns: addOnSelections.length
          ? addOnSelections.map((a) => ({
              name: a.name,
              sku: a.sku,
              price: a.unitPrice,
              quantity: a.quantity,
            }))
          : undefined,
        wrap: {
          productId: wrapSelection.productId,
          productTitle: wrapSelection.productTitle,
          variantId: wrapSelection.variantId,
          variantTitle: wrapSelection.variantTitle,
          sku: wrapSelection.sku || undefined,
        },
      });
    },
    onSuccess: () => {
      navigate(`/clinics/${clinicId}`);
    },
  });

  const patientOptions = [
    { label: '— Select a patient —', value: '' },
    ...((patientsData?.data ?? []).map((p: any) => ({
      label: `${p.firstName} ${p.lastName}${p.email ? ` (${p.email})` : ''}`,
      value: p.id,
    }))),
  ];

  const productOptions = [
    { label: '— Select a machine —', value: '' },
    ...machines.map((p) => ({ label: p.title, value: p.shopifyProductId })),
  ];

  if (productsLoading || wrapsLoading || patientsLoading) {
    return (
      <Page title="Create Order on Behalf of Clinic">
        <Card>
          <SkeletonBodyText lines={10} />
        </Card>
      </Page>
    );
  }

  return (
    <Page
      title="Create Order on Behalf of Clinic"
      subtitle={clinic?.name}
      backAction={{
        content: 'Back to Clinic',
        onAction: () => navigate(`/clinics/${clinicId}`),
      }}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {createMutation.isError && (
              <Banner tone="critical">
                <p>
                  {(createMutation.error as any)?.response?.data?.message ||
                    'Failed to create order'}
                </p>
              </Banner>
            )}
            {createMutation.isSuccess && (
              <Banner tone="success">
                <p>
                  Order created.{' '}
                  {createMutation.data?.invoiceUrl && (
                    <a
                      href={createMutation.data.invoiceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View invoice
                    </a>
                  )}
                </p>
              </Banner>
            )}

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  Patient
                </Text>
                <Select
                  label="Patient"
                  options={patientOptions}
                  value={patientId}
                  onChange={setPatientId}
                  helpText={
                    (patientsData?.data ?? []).length === 0
                      ? 'No patients on file. Create a patient on the clinic first.'
                      : undefined
                  }
                  requiredIndicator
                />
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  Machine
                </Text>
                <Select
                  label="Game Ready system"
                  options={productOptions}
                  value={productId}
                  onChange={(val) => {
                    setProductId(val);
                    setVariantId('');
                  }}
                  requiredIndicator
                />
                {product && variants.length > 1 && (
                  <Select
                    label="Variant"
                    options={variants.map((v) => ({ label: v.title, value: v.id }))}
                    value={variantId || variants[0]?.id}
                    onChange={setVariantId}
                  />
                )}
                {product && (
                  <Text as="p" tone="success">
                    ${weeklyRateDollars.toFixed(2)} CAD / week (min {minWeeks} weeks)
                  </Text>
                )}
                <InlineStack gap="400">
                  <Box width="50%">
                    <TextField
                      label="Surgery date"
                      type="date"
                      value={surgeryDate}
                      onChange={setSurgeryDate}
                      autoComplete="off"
                      requiredIndicator
                    />
                  </Box>
                  <Box width="50%">
                    <Select
                      label="Rental weeks"
                      options={WEEKS_OPTIONS}
                      value={rentalWeeks}
                      onChange={setRentalWeeks}
                      requiredIndicator
                    />
                  </Box>
                </InlineStack>
                {bilateralPriceDollars > 0 && (
                  <Checkbox
                    label={`Bilateral ($${bilateralPriceDollars.toFixed(2)} CAD one-time)`}
                    checked={isBilateral}
                    onChange={setIsBilateral}
                  />
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  Wrap <Text as="span" tone="critical">*</Text>
                </Text>
                <WrapPicker
                  wraps={wraps}
                  selectedSku={wrapSelection?.sku ?? ''}
                  onChange={setWrapSelection}
                />
                {(addOnProducts?.length ?? 0) > 0 && (
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm">Add-ons</Text>
                    <AddOnPicker
                      addons={addOnProducts ?? []}
                      selections={addOnSelections}
                      onChange={setAddOnSelections}
                      rateType="wholesale"
                    />
                  </BlockStack>
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  Fulfilment
                </Text>
                {(clinic as any)?.isConsignmentClinic && (
                  <Checkbox
                    label="Clinic fulfils from on-site consignment stock"
                    checked={isClinicFulfil}
                    onChange={setIsClinicFulfil}
                    helpText="Skips warehouse dispatch — clinic ships from their own inventory."
                  />
                )}
                {!isClinicFulfil && (
                  <Select
                    label="Ship to"
                    options={[
                      { label: 'Clinic address', value: 'clinic' },
                      { label: 'Patient address', value: 'patient' },
                    ]}
                    value={shippingAddressType}
                    onChange={(val) =>
                      setShippingAddressType(val as 'clinic' | 'patient')
                    }
                  />
                )}
                <TextField
                  label="Special instructions (optional)"
                  value={specialInstructions}
                  onChange={setSpecialInstructions}
                  multiline={2}
                  autoComplete="off"
                />
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text as="span">
                    Rental ({weeks} weeks × ${weeklyRateDollars.toFixed(2)})
                  </Text>
                  <Text as="span">${rentalTotal.toFixed(2)} CAD</Text>
                </InlineStack>
                {isBilateral && (
                  <InlineStack align="space-between">
                    <Text as="span">Bilateral</Text>
                    <Text as="span">${bilateralTotal.toFixed(2)} CAD</Text>
                  </InlineStack>
                )}
                <Divider />
                <InlineStack align="space-between">
                  <Text as="span" fontWeight="bold">
                    Subtotal
                  </Text>
                  <Text as="span" fontWeight="bold">
                    ${subtotal.toFixed(2)} CAD
                  </Text>
                </InlineStack>
                <Box paddingBlockStart="300">
                  <Button
                    variant="primary"
                    fullWidth
                    disabled={!isValid}
                    loading={createMutation.isPending}
                    onClick={() => createMutation.mutate()}
                  >
                    Create Order
                  </Button>
                </Box>
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
